import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import CalendarPicker from '../util/CalendarPicker';
import SelectUsers from '../docsComponents/SelectUsers';


interface CarFormProps {
  carId: number | null;
  onClose: () => void;
  onSaved: () => void;
  baseUrl: string;
}

const CarForm: React.FC<CarFormProps> = ({ carId, onClose, onSaved, baseUrl }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = carId != null;

  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [plate, setPlate] = useState('');
  const [year, setYear] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [mileage, setMileage] = useState('');
  const [vin, setVin] = useState('');
  const [costPerKm, setCostPerKm] = useState('');
  const [avgFuel, setAvgFuel] = useState('');
  const [usage, setUsage] = useState('');
  const [active, setActive] = useState(true);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [motExpiry, setMotExpiry] = useState<Date | null>(null);
  const [motFile, setMotFile] = useState<File | null>(null);
  const [motDocUrl, setMotDocUrl] = useState('');
  const [allDrivers, setAllDrivers] = useState(false);
  const [driverIds, setDriverIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const loadCar = useCallback(async () => {
    if (carId == null) return;
    try {
      const { data } = await axios.get(`cars/${carId}`);
      if (!data) return;
      setBrand(data.brand || '');
      setModel(data.model || '');
      setPlate(data.plate_number || '');
      setYear(data.year != null ? String(data.year) : '');
      setFuelType(data.fuel_type || '');
      setMileage(data.mileage != null ? String(data.mileage) : '');
      setVin(data.vin || '');
      setCostPerKm(data.cost_per_km != null ? String(data.cost_per_km) : '');
      setAvgFuel(
        data.avg_fuel_consumption != null ? String(data.avg_fuel_consumption) : '',
      );
      setUsage(data.usage_type || '');
      setActive(data.is_active ?? true);
      setAdditionalInfo(data.additional_info || '');
      setMotExpiry(data.mot_expiry_date ? new Date(data.mot_expiry_date) : null);
      setMotDocUrl(data.mot_certificate_url || '');
      setAllDrivers(!!data.all_drivers);
      setDriverIds(Array.isArray(data.driver_ids) ? data.driver_ids : []);
    } catch {
      // ошибки авторизации обрабатывает глобальный interceptor
    }
  }, [carId]);

  useEffect(() => {
    loadCar();
  }, [loadCar]);

  const num = (v: string) => (v.trim() === '' ? null : Number(v));

  const handleSave = async () => {
    if (!brand.trim() || !model.trim() || !plate.trim()) {
      enqueueSnackbar(t('FillRequiredFields') || 'Fill required fields', {
        variant: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        brand: brand.trim(),
        model: model.trim(),
        plate_number: plate.trim(),
        year: num(year),
        fuel_type: fuelType || null,
        mileage: num(mileage),
        vin: vin || null,
        cost_per_km: num(costPerKm),
        avg_fuel_consumption: num(avgFuel),
        usage_type: usage || null,
        is_active: active,
        additional_info: additionalInfo || null,
        mot_expiry_date: motExpiry,
        all_drivers: allDrivers,
        driver_ids: allDrivers ? [] : driverIds,
      };

      let savedId = carId;
      if (isEdit) {
        await axios.put(`cars/${carId}`, payload);
      } else {
        const { data } = await axios.post('cars/add', payload);
        savedId = data?.id;
      }

      if (motFile && savedId) {
        const fd = new FormData();
        fd.append('mot_certificate', motFile);
        await axios.post(`cars/upload_mot/${savedId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      enqueueSnackbar(t('Saved') || 'Saved', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || t('ErrorSaving') || 'Error saving',
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fx-modal" role="presentation">
      {/* Header */}
      <div className="fx-head">
        <div className="fx-head-text">
          <div className="fx-head-title">
            {isEdit ? t('EditVehicle') || 'Edit vehicle' : t('AddVehicle') || 'Add vehicle'}
          </div>
        </div>
        <div className="fx-close" onClick={onClose}><CloseIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className="fx-body">
        <div className="fx-row2">
          <div className="fx-field">
            <input className="fx-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={`${t('Brand')} *`} />
          </div>
          <div className="fx-field">
            <input className="fx-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder={`${t('Model')} *`} />
          </div>
        </div>

        <div className="fx-row2">
          <div className="fx-field">
            <input className="fx-input" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder={`${t('PlateNumber')} *`} />
          </div>
          <div className="fx-field">
            <input title={t('Year') as string} className="fx-input" value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, ''))} placeholder={t('Year')} />
          </div>
        </div>

        <div className="fx-row2">
          <div className="fx-field">
            <select title={t('FuelType') as string} className="fx-input" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
              <option value="">{t('FuelType')}</option>
              <option value="petrol">{t('Petrol') || 'Petrol'}</option>
              <option value="diesel">{t('Diesel') || 'Diesel'}</option>
              <option value="electric">{t('Electric') || 'Electric'}</option>
              <option value="hybrid">{t('Hybrid') || 'Hybrid'}</option>
            </select>
          </div>
          <div className="fx-field">
            <input title={t('Mileage') as string} className="fx-input" value={mileage} onChange={(e) => setMileage(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('Mileage')} />
          </div>
        </div>

        <div className="fx-row2">
          <div className="fx-field">
            <input title={t('CostPerKm') as string} className="fx-input" value={costPerKm} onChange={(e) => setCostPerKm(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('CostPerKm')} />
          </div>
          <div className="fx-field">
            <input title={t('AvgFuelConsumption') as string} className="fx-input" value={avgFuel} onChange={(e) => setAvgFuel(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('AvgFuelConsumption') || 'Avg fuel'} />
          </div>
        </div>

        <div className="fx-row2">
          <div className="fx-field">
            <select title={t('UsageType') as string} className="fx-input" value={usage} onChange={(e) => setUsage(e.target.value)}>
              <option value="">{t('UsageType')}</option>
              <option value="personal">{t('Personal') || 'Personal'}</option>
              <option value="business">{t('Business') || 'Business'}</option>
              <option value="mixed">{t('Mixed') || 'Mixed'}</option>
            </select>
          </div>
          <div className="fx-field">
            <input title={t('Vin') as string} className="fx-input" value={vin} onChange={(e) => setVin(e.target.value)} placeholder={t('Vin') || 'VIN'} />
          </div>
        </div>

        {/* MOT */}
        <div className="fx-section">{t('MOTInfo') || 'MOT'}</div>
        <div className="fx-row2" style={{ alignItems: 'flex-end' }}>
          <div className="fx-field">
            <CalendarPicker
              mode="single"
              variant="bare"
              className="fx-input"
              value={motExpiry}
              onChange={(date: any) => setMotExpiry(date)}
              displayFormat="DD/MM/YYYY"
              clearable
              placeholder={t('MotExpiryDate') || 'MOT expiry'}
            />
          </div>
          <div className="fx-field">
            <label className="fx-dropzone">
              <div className="fx-dz-icon"><UploadIcon fontSize="small" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fx-dz-main truncate">
                  {motFile ? motFile.name : (motDocUrl ? (t('CurrentDocument') || 'Current document') : (t('BrowseMot') || 'MOT doc'))}
                </div>
                <div className="fx-dz-sub">PDF</div>
              </div>
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => setMotFile(e.target.files?.[0] || null)} />
            </label>
            {!motFile && motDocUrl && (
              <a href={`${baseUrl}${motDocUrl}`} target="_blank" rel="noopener noreferrer" className="fx-dz-sub underline" style={{ marginTop: 4 }}>
                📄 {t('CurrentDocument') || 'Current document'}
              </a>
            )}
          </div>
        </div>

        {/* Drivers */}
        <div className="fx-section">{t('Drivers') || 'Drivers'}</div>
        <div className="fx-switch-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          <label className="flex items-center gap-[8px] text-[13px] cursor-pointer">
            <input type="checkbox" checked={allDrivers} onChange={(e) => setAllDrivers(e.target.checked)} />
            {t('AnyEmployee') || 'Any employee can drive'}
          </label>
          {!allDrivers && (
            <SelectUsers selectedTasks={driverIds} setSelectedTasks={setDriverIds} />
          )}
        </div>

        <div className="fx-field">
          <input title={t('AdditionalInfo') as string} className="fx-input" value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} placeholder={t('AdditionalInfo')} />
        </div>

        <div className="fx-switch-card">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} style={{ marginTop: 3 }} />
          <div>
            <div className="fx-switch-title cursor-pointer" onClick={() => setActive(!active)}>{t('Active') || 'Active'}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fx-foot">
        <button type="button" className="fx-save" disabled={saving} onClick={handleSave}>
          {isEdit ? t('Save') || 'Save' : t('Add') || 'Add'}
        </button>
        <button type="button" className="fx-cancel" onClick={onClose}>
          {t('Cancel') || 'Cancel'}
        </button>
      </div>
    </div>
  );
};

export default CarForm;
